/**
 * SEARCH-002 — Sous-hook « navigation + aide + email + scope » de la palette (⌘K).
 *
 * Extrait mécaniquement de `SearchPalette.tsx` / `useSearchPalette` (aucun
 * changement de logique) : handlers `go`/`openHit`/`handleHelp`/`handleBrowseHelp`,
 * gestion des recents (clear/remove), aperçu plein écran, compositeur d'email,
 * pose de scope. Appelé INCONDITIONNELLEMENT par `useSearchPalette` à sa position
 * d'origine (bloc de `useCallback` contigu → ordre des hooks préservé).
 */

import { type Dispatch, type RefObject, type SetStateAction, useCallback } from 'react'
import { type NavigateFunction } from 'react-router-dom'
import { CATEGORY_REGISTRY, type SearchHit, type SourceType } from '@/lib/searchCategories'
import { writeRecentQueries, type QuickLookTarget } from './searchPaletteHelpers'

export interface UseSearchPaletteHandlersArgs {
    close: () => void
    navigate: NavigateFunction
    inputRef: RefObject<HTMLInputElement>
    openWithHelpSearch: (query: string) => void
    openHelp: () => void
    input: string
    userId: string
    rememberQuery: () => void
    setRecents: Dispatch<SetStateAction<string[]>>
    setQuickLook: Dispatch<SetStateAction<QuickLookTarget | null>>
    setComposeEmail: Dispatch<SetStateAction<{ email: string; name: string } | null>>
    setScopeType: Dispatch<SetStateAction<SourceType | null>>
}

export function useSearchPaletteHandlers({
    close,
    navigate,
    inputRef,
    openWithHelpSearch,
    openHelp,
    input,
    userId,
    rememberQuery,
    setRecents,
    setQuickLook,
    setComposeEmail,
    setScopeType,
}: UseSearchPaletteHandlersArgs) {
    const go = useCallback(
        (route: string) => {
            close()
            // On arrive sur une NOUVELLE page : le champ ne doit PAS garder le focus
            // (sinon curseur clignotant trompeur — on croit pouvoir taper). Pour
            // relancer : ⌘K ou clic explicite sur le champ.
            inputRef.current?.blur()
            navigate(route)
        },
        [close, navigate, inputRef],
    )
    const openHit = useCallback(
        (hit: SearchHit) => {
            rememberQuery()
            go(CATEGORY_REGISTRY[hit.type].route(hit))
        },
        [go, rememberQuery],
    )
    // On FERME la palette puis on ouvre le drawer à la FRAME SUIVANTE : la palette
    // libère d'abord le focus (sinon sa fermeture « vole » le focus que le drawer
    // vient de poser sur son champ → l'utilisateur devait recliquer). L'`input` est
    // capturé dans la closure (le reset post-fermeture ne l'affecte pas).
    // AIDE UNIFIÉE (complément « bande basse » §4) : la palette ne force plus de mode
    // (plus de `kind:'assistant'`). UN seul canal → le champ unifié du drawer, qui
    // tranche lui-même article (lexical) vs réponse IA.
    //  • requête saisie → préremplit la recherche du drawer (`openWithHelpSearch`) ;
    //  • champ vide      → ouvre simplement le drawer (un texte vide serait ignoré).
    const handleHelp = useCallback(() => {
        rememberQuery()
        close()
        requestAnimationFrame(() => openWithHelpSearch(input))
    }, [close, openWithHelpSearch, input, rememberQuery])
    const handleBrowseHelp = useCallback(() => {
        close()
        requestAnimationFrame(() => openHelp())
    }, [close, openHelp])
    const clearRecents = useCallback(() => {
        writeRecentQueries(userId, [])
        setRecents([])
    }, [userId, setRecents])
    // Supprime UNE recherche récente ciblée (≠ tout effacer). Maj localStorage + état.
    const removeRecent = useCallback(
        (q: string) => {
            setRecents((prev) => {
                const next = prev.filter((r) => r !== q)
                writeRecentQueries(userId, next)
                return next
            })
        },
        [userId, setRecents],
    )
    const openQuickLook = useCallback(
        (target: QuickLookTarget) => setQuickLook(target),
        [setQuickLook],
    )
    // Compositeur d'email (contact/apprenant). Gating : permission `directory:bulk_email`
    // (+ plan BUSINESS, vérifié côté backend à l'envoi). L'envoi est tracé dans
    // l'historique d'emails GÉNÉRAL (`email_type='directory_bulk'`), pas rattaché à une
    // fiche — comme un envoi en masse depuis l'annuaire.
    // UNGATED (décision PO) : envoyer un email depuis la recherche est ouvert à TOUS
    // les utilisateurs — pas de permission ni de plan (anti-abus = rate-limit backend).
    const canSendEmail = true
    // On FERME la palette PUIS on ouvre le compositeur à la frame suivante (même
    // pattern que l'ouverture du drawer d'aide). Sinon le Dialog modal jaillit
    // PAR-DESSUS le Popover encore ouvert → conflit de focus + course sur le clic
    // d'ouverture → il se referme instantanément. `composeEmail` n'est PAS remis à
    // zéro par le reset de fermeture (sinon il s'effacerait juste après).
    const openComposeEmail = useCallback(
        (target: { email: string; name: string }) => {
            close()
            requestAnimationFrame(() => setComposeEmail(target))
        },
        [close, setComposeEmail],
    )
    // Pose un jeton de scope ET re-focus le champ : on peut taper tout de suite
    // après avoir cliqué un filtre (sans rebouger la souris vers le champ).
    const pickScope = useCallback(
        (type: SourceType) => {
            setScopeType(type)
            requestAnimationFrame(() => inputRef.current?.focus())
        },
        [setScopeType, inputRef],
    )

    return {
        go,
        openHit,
        handleHelp,
        handleBrowseHelp,
        clearRecents,
        removeRecent,
        openQuickLook,
        canSendEmail,
        openComposeEmail,
        pickScope,
    }
}
